import React, { useEffect, useState } from "react";
import { FaLink, FaDollarSign, FaExclamationTriangle, FaBoxOpen } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/Auth";
import { useGlobalContext } from "../context/stateContext";
import { IoIosTrendingUp } from "react-icons/io";
import StatCard from "../components/StatCard";
import ExpiryAlertModal from "../components/modals/ExpiryAlertModal";
import { BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { toast } from "react-toastify";
const Dashboard = () => {
  const navigate = useNavigate();
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [gettingDashbaordData, setGettingDashboardData] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salesTrendData, setSalesTrendData] = useState([]);
  const [branchPerformanceData, setBranchPerformanceData] = useState([]);

  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#ec4899",
    "#06b6d4",
    "#84cc16",
    "#f97316",
    "#6366f1",
    "#14b8a6",
    "#d946ef",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#ec4899",
    "#06b6d4",
    "#84cc16",
    "#f97316",
    "#6366f1",
    "#14b8a6",
    "#d946ef",
  ];

  const getDashbaordData = async () => {
    setGettingDashboardData(true);
    try {
      const data = await requestWithAuth(`/users/dashboard`, "GET");
      setDashboardData(data);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setGettingDashboardData(false);
    }
  };

  const getSalesTrendData = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(`/report/sales-trend/`, "GET");
      setSalesTrendData(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error("Failed to load sales trend data");
    } finally {
      setLoading(false);
    }
  };

  const getBranchPerformance = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(
        `/report/branch-performance/`,
        "GET"
      );
      setBranchPerformanceData(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error("Failed to load performance data");
    } finally {
      setLoading(false);
    }
  };

  // Check if alert should be shown
  useEffect(() => {
    const checkExpiryAlert = () => {
      const today = new Date().toISOString().split("T")[0];
      const dismissedDate = localStorage.getItem("expiryAlertDismissed");
      const hasExpiryIssues =
        dashboardData?.expiring_drugs > 0 || dashboardData?.expired_drugs > 0;
      const shouldShow = hasExpiryIssues && dismissedDate !== today;

      if (shouldShow) {
        setTimeout(() => {
          setShowExpiryAlert(true);
        }, 500);
      }
    };

    if (!gettingDashbaordData) {
      checkExpiryAlert();
    }
  }, [
    gettingDashbaordData,
    dashboardData?.expiring_drugs,
    dashboardData?.expired_drugs,
  ]);

  const handleCloseExpiryAlert = () => {
    setShowExpiryAlert(false);
  };

  const handleCloseAndHideExpiryAlert = () => {
    if (dashboardData?.expired_drugs > 0) {
      toast.error(
        `You have ${dashboardData?.expired_drugs} expired drugs, please clear them before continuing`
      );
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    // localStorage.setItem("expiryAlertDismissed", today);
    setShowExpiryAlert(false);
  };

  const handleViewExpiringDrugs = () => {
    const branch_id = activeSession?.branch?.id;
    if (dashboardData?.expired_drugs === 0) {
      const today = new Date().toISOString().split("T")[0];
      // localStorage.setItem("expiryAlertDismissed", today);
    }
    setShowExpiryAlert(false);
    navigate(`/inventory/branch/expiring-drugs/${branch_id}`);
  };

  useEffect(() => {
    getDashbaordData();
    getSalesTrendData();
    getBranchPerformance();
  }, []);


  const SimpleCard = ({ title, value, linkTo, color = "bg-gray-100", subtitle }) => (
    <div className={`rounded-lg p-6 ${color} text-center`}>
      <h3 className="text-3xl font-bold mb-2">{value}</h3>
      <p className="text-sm opacity-90 mb-3">{title}</p>
      {subtitle && (
        <p className="text-xs opacity-75 mb-3">{subtitle}</p>
      )}
      {linkTo && (
        <Link to={linkTo} className="inline-flex items-center gap-1 text-sm hover:underline">
          View Details <FaLink className="text-[12px]" />
        </Link>
      )}
    </div>
  );

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCount = (count) => {
    return Number(count || 0).toLocaleString('en-US');
  };

  if (gettingDashbaordData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome - {activeSession?.user?.first_name}
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-200 rounded-lg h-32 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      <ExpiryAlertModal
        isOpen={showExpiryAlert}
        onClose={handleCloseExpiryAlert}
        onHideModal={handleCloseAndHideExpiryAlert}
        expiringDrugs={dashboardData.expiring_drugs}
        expiredDrugs={dashboardData.expired_drugs}
        onViewDetails={handleViewExpiringDrugs}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Welcome {activeSession?.user?.first_name}
        </h1>
        <p className="text-gray-600">Here's what's happening with your business today</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <StatCard
          title="Today's Sales"
          value={`GHS ${formatAmount(dashboardData?.sales)}`}
          subtitle={`Credit Repayment: GHs ${formatAmount(dashboardData?.credit_repayments)} (${formatCount(dashboardData?.number_credit_repayments)})`}
          additionalInfo={`Customers Served: GHs ${formatAmount(dashboardData?.customers_served)} (${formatCount(dashboardData?.number_customers_served)})`}
          icon={FaDollarSign}
          color="bg-blue-500"
          linkTo="/sales/today"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Outstanding Invoices"
          value={`GHS ${formatAmount(dashboardData?.outstanding_invoices)}`}
          subtitle="Outstanding Supplier Invoices"
          additionalInfo={`Total Suppliers: ${formatCount(dashboardData?.total_suppliers)}`}
          icon={FaExclamationTriangle}
          color="bg-red-500"
          linkTo="/transaction/supplier"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Low Stock Alert"
          value={formatCount(dashboardData?.items_below_reorder)}
          subtitle="Items Below reorder level"
          icon={FaBoxOpen}
          color="bg-yellow-500"
          linkTo="/inventory/reorder"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Stock Value"
          value={`GHS ${formatAmount(dashboardData?.total_stock_outstanding)}`}
          subtitle="Total Stock Outstanding"
          additionalInfo={`Drug Stock: ${formatAmount(dashboardData?.drug_stock)}`}
          icon={IoIosTrendingUp}
          color="bg-green-500"
          linkTo="/stock/outstanding"
          onRefresh={getDashbaordData}
        />

        <StatCard
          title="Expiry Alert"
          value={formatCount(dashboardData?.expiring_drugs)}
          subtitle="Items that are about to expire"
          additionalInfo={dashboardData?.expired_drugs > 0 ? `${dashboardData?.expired_drugs} items expired` : "No items expired"}
          icon={FaExclamationTriangle}
          color="bg-red-600"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
          onRefresh={getDashbaordData}
        />
      </div>

      {/* Secondary Stats */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Branch/Financial Management</h2>
        <p className="text-sm text-gray-600 mb-4">Transaction Summary</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SimpleCard
          title="Pending Item Returns"
          value={formatCount(dashboardData?.pending_returns)}
          subtitle="Items pending return"
          linkTo="/pos/return"
          color="bg-white border border-gray-200 text-gray-800"
        />

        <SimpleCard
          title="Invoice Report"
          value={formatCount(dashboardData?.invoice_report)}
          linkTo="/report/purchases/invoice"
          color="bg-teal-500 text-white"
        />

        <SimpleCard
          title="Pending Requisition"
          value={formatCount(dashboardData?.pending_requisitions)}
          subtitle=""
          linkTo="/authorize/requisition"
          color="bg-purple-500 text-white"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 my-8">
        {/* Sales Trend Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 size={20} className="text-blue-600" />
                Sales Trend Analysis
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Monthly performance vs last year
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesTrendData || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="sales"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Current Year"
              />
              <Line
                type="monotone"
                dataKey="last_year"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Last Year"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Branch Performance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Branch Performance
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Sales distribution by branch this year
              </p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={branchPerformanceData || []}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
                nameKey="name"
              >
                {branchPerformanceData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
                formatter={(value, name) => [`GHS ${Number(value).toFixed(2)}`, name]}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <span className="text-gray-600 font-medium ml-1">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;